import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, CalendarPlus, FileText, Loader2, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useClientSearchV2 } from '@/hooks/useClientSearchV2'
import { useClientJobSitesV2 } from '@/hooks/useClientJobSitesV2'
import { createProjectV2, DuplicateDetectionException } from '@/lib/api/v2/projects'
import { createProjectQuoteDraftV2 } from '@/components/features/project-detail/quotes/quotes-v2.api'
import { cn } from '@/lib/utils'
import {
  INTERVENTION_CONSTRUCTION_TYPES,
  buildInterventionPayload,
  canSubmitIntervention,
  missingForIntervention,
  interventionLandingRoute,
  quoteDraftRoute,
  type CivilityChoice,
  type InterventionExit,
  type InterventionFormState,
} from '@/components/features/intervention-create/intervention-payload'
import { showErrorToast } from '@/lib/toast'

/**
 * SOLY-REGIE-001 MS-03 — « Nouvelle intervention ».
 *
 * One screen, five fields, two exits. The gesture is crossed ~600 times a year against ~50 for the
 * three-step wizard: this is the main entry point of the product, not a secondary one. Anything
 * that costs a click here costs it 600 times.
 *
 * No stepper, no progress bar, no auto-save — a stepper over five fields is the signature of a form
 * that does not own its own brevity.
 */
export default function InterventionNewPage() {
  const navigate = useNavigate()

  const [query, setQuery] = useState('')
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [customerLabel, setCustomerLabel] = useState<string>('')
  const [creatingCustomer, setCreatingCustomer] = useState(false)
  const [newLastName, setNewLastName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [newCivility, setNewCivility] = useState<CivilityChoice | null>(null)

  const [jobSiteId, setJobSiteId] = useState<string | null>(null)
  const [street, setStreet] = useState('')
  const [streetNumber, setStreetNumber] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [city, setCity] = useState('')

  const [description, setDescription] = useState('')
  const [constructionType, setConstructionType] = useState('')
  const [submitting, setSubmitting] = useState<InterventionExit | null>(null)

  const { results, isLoading: searching } = useClientSearchV2({ q: customerId ? '' : query, take: 8 })
  const { jobSites, isLoading: loadingJobSites } = useClientJobSitesV2({ customerId, take: 50 })

  // A known client with exactly ONE job site: pre-select it. She called about that address in the
  // overwhelming majority of cases, and confirming a single radio 600 times a year is pure toll.
  useEffect(() => {
    if (jobSites.length === 1) {
      setJobSiteId(jobSites[0].jobSiteId ?? jobSites[0].id ?? null)
    }
  }, [jobSites])

  const needsAddress = customerId == null || (!loadingJobSites && jobSites.length === 0) || jobSiteId === '__new__'

  const state: InterventionFormState = useMemo(() => ({
    customerId,
    newCustomer: customerId == null ? { lastName: newLastName, phone: newPhone, civility: newCivility } : null,
    jobSiteId: jobSiteId === '__new__' ? null : jobSiteId,
    newAddress: needsAddress && (street.trim() || city.trim())
      ? { street, number: streetNumber, postalCode, city }
      : null,
    description,
    constructionType,
  }), [customerId, newLastName, newPhone, newCivility, jobSiteId, needsAddress, street, streetNumber, postalCode, city, description, constructionType])

  const missing = useMemo(() => missingForIntervention(state), [state])
  const canSubmit = canSubmitIntervention(state) && submitting == null

  const submit = async (exit: InterventionExit, allowDuplicate = false) => {
    // A4 (revue 2026-08-19) — le desarmement du bouton est un fait de RENDU, pas une garantie.
    // Deux clics rapides sur une API lente = deux dossiers, et deux brouillons de devis sur la
    // sortie devis. `allowDuplicate` est la reentrance legitime : elle vient de nous, pas d'un clic.
    if (submitting != null && !allowDuplicate) return
    if (!canSubmitIntervention(state)) return
    setSubmitting(exit)
    try {
      const response = await createProjectV2(buildInterventionPayload({ ...state, allowDuplicate }, exit))

      // [Faire un devis] promises a quote, so it opens one. Creating the draft here rather than
      // making her click « créer un devis » on the next page saves the click on ~270 files a year,
      // and keeps the two doors under one rule: each lands where its work is done.
      if (exit === 'quote') {
        try {
          const quote = await createProjectQuoteDraftV2(response.projectId)
          const route = quoteDraftRoute(response.projectId, quote)
          if (route) {
            navigate(route)
            return
          }
        } catch {
          // The FILE exists — only the draft failed. Landing on its page is a degradation she can
          // recover from in one click, never a dead end, and never a lost intervention. But she
          // clicked « Faire un devis »: arriving on a file with no quote and no explanation would
          // leave her wondering what she did wrong (revue 2026-08-19).
          showErrorToast("Intervention créée, mais le devis n'a pas pu être ouvert — créez-le depuis la fiche.")
        }
      }

      navigate(interventionLandingRoute(response.projectId, exit))
    } catch (error) {
      if (error instanceof DuplicateDetectionException) {
        // The endpoint owns duplicate detection (CheckDuplicatesAsync → 409). On this form the
        // answer is almost always « yes it is the same client, she is calling again » — so we ask
        // once, inline, instead of dragging the wizard's modal in.
        // C1 (QA 2026-08-19) — the 409 fires on a look-alike CLIENT *or* an already-known ADDRESS
        // (CheckDuplicatesAsync tests job sites as soon as the address is new, whatever the client
        // mode). The old wording claimed a client every time, which was false — and undecidable —
        // when what matched was the street.
        const dup = error.data?.duplicates
        const clients = dup?.clients?.items ?? []
        const sites = dup?.jobSites ?? []
        const what = clients.length > 0 && sites.length > 0
          ? `un client (${clients[0]?.name ?? '?'}) et une adresse déjà connus`
          : clients.length > 0
            ? `un client existant (${clients[0]?.name ?? '?'})`
            : `une adresse déjà enregistrée${sites[0]?.label ? ` (${sites[0].label})` : ''}`
        if (window.confirm(`Soly a trouvé ${what}. Créer quand même une nouvelle fiche ?`)) {
          await submit(exit, true)
          return
        }
      } else {
        // The form is NOT cleared: she has the client on the phone, and losing five fields is worse
        // than the error itself.
        showErrorToast("Impossible de créer l'intervention")
        console.error(error)
      }
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="intervention-new-back-btn" aria-label="Retour">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold">Nouvelle intervention</h1>
          <p className="text-sm text-muted-foreground">Une retouche, un passage — pas un chantier complet.</p>
        </div>
      </div>

      {/* ── Client ─────────────────────────────────────────────────────── */}
      <section className="space-y-2">
        <Label>Client *</Label>
        {customerId ? (
          <div className="flex items-center justify-between rounded-lg border p-3">
            <span className="text-sm font-medium">{customerLabel}</span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="intervention-client-change-btn"
              // C5 (QA 2026-08-19) — the address goes with the client. Keeping it would silently
              // create client A's job site on client B's file, the next time she picks a second
              // client who also has no known site.
              onClick={() => {
                setCustomerId(null); setCustomerLabel(''); setJobSiteId(null)
                setStreet(''); setStreetNumber(''); setPostalCode(''); setCity('')
                // ... et le brouillon de client neuf : sinon repasser par « Nouveau client » fait
                // resurgir le nom et le telephone du precedent (revue 2026-08-19).
                setNewLastName(''); setNewPhone(''); setNewCivility(null)
              }}
            >
              Changer
            </Button>
          </div>
        ) : creatingCustomer ? (
          <div className="space-y-3 rounded-lg border p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="new-client-name" className="text-xs">Civilité et nom *</Label>
                <div className="flex gap-1.5">
                  {/* Shares the name's row: the civility costs a click, never a line. It is required
                      because « M. » invented by default is printed on the client's invoice. */}
                  <div
                    role="radiogroup"
                    aria-label="Civilité"
                    className={cn(
                      'flex flex-none overflow-hidden rounded-md border',
                      // Required, so it SAYS it is: a form that greys its buttons without telling
                      // which field is missing makes her hunt (revue 2026-08-19).
                      newCivility === null && 'border-destructive',
                    )}
                  >
                    {(['mr', 'mrs'] as const).map((c) => (
                      <button
                        key={c}
                        type="button"
                        data-testid={`intervention-client-civility-${c}`}
                        role="radio"
                        aria-checked={newCivility === c}
                        onClick={() => setNewCivility(c)}
                        className={cn(
                          'px-2 text-xs transition',
                          newCivility === c ? 'bg-primary text-primary-foreground' : 'hover:bg-muted',
                        )}
                      >
                        {c === 'mr' ? 'M.' : 'Mme'}
                      </button>
                    ))}
                  </div>
                  <Input id="new-client-name" data-testid="intervention-client-name-input" value={newLastName} onChange={(e) => setNewLastName(e.target.value)} placeholder="Vandenberghe" />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-client-phone" className="text-xs">Téléphone</Label>
                <Input id="new-client-phone" data-testid="intervention-client-phone-input" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="0475 12 34 56" />
              </div>
            </div>
            <Button variant="ghost" size="sm" data-testid="intervention-client-search-back-btn" onClick={() => setCreatingCustomer(false)}>
              <Search className="mr-1 h-3 w-3" /> Chercher un client existant
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Input
              data-testid="intervention-client-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un client…"
            />
            {searching && <p className="text-xs text-muted-foreground">Recherche…</p>}
            {results.length > 0 && (
              <div className="divide-y rounded-lg border">
                {results.map((r) => {
                  const id = r.customerId ?? ''
                  const contact = r.contacts?.find((c) => c.isPrimary) ?? r.contacts?.[0]
                  const label = r.name || [contact?.firstName, contact?.lastName].filter(Boolean).join(' ') || 'Client'
                  return (
                    <button
                      key={id}
                      type="button"
                      data-testid={`intervention-client-option-${id}`}
                      className="flex w-full items-center justify-between p-2 text-left text-sm hover:bg-muted"
                      onClick={() => { setCustomerId(id); setCustomerLabel(label); setQuery('') }}
                    >
                      <span>{label}</span>
                      <span className="text-xs text-muted-foreground">{contact?.phone}</span>
                    </button>
                  )
                })}
              </div>
            )}
            <Button variant="outline" size="sm" data-testid="intervention-client-new-btn" onClick={() => setCreatingCustomer(true)}>
              <Plus className="mr-1 h-3 w-3" /> Nouveau client
            </Button>
          </div>
        )}
      </section>

      {/* ── Chantier ───────────────────────────────────────────────────── */}
      <section className="space-y-2">
        <Label>Chantier *</Label>
        {customerId && jobSites.length > 0 && (
          <RadioGroup value={jobSiteId ?? ''} onValueChange={setJobSiteId} className="space-y-2">
            {jobSites.map((js) => {
              const id = js.jobSiteId ?? js.id ?? ''
              return (
                <div key={id} className="flex items-center gap-2 rounded-lg border p-3">
                  <RadioGroupItem value={id} id={`js-${id}`} data-testid={`intervention-jobsite-option-${id}`} />
                  {/* QA staging 2026-08-20 — the label ALONE is not enough to tell two job sites
                      apart. Labels default to the city, so a client with two properties in the same
                      commune (the parents, the daughter, the second flat — the classic case of the
                      loyal client, who is precisely the one calling back for a retouche) got two
                      radios both reading « Jodoigne ». The address was in the very same API
                      response. She picks one at random and the placeur drives to the wrong house. */}
                  {/* `flex-col items-start`: Label's base class is `flex items-center gap-2`, which
                      blockifies the two spans into flex ITEMS — `block` on them is inert, and the
                      address would render beside the label instead of under it, pushing the row on
                      a long street name. The column has to be asked for explicitly. */}
                  <Label htmlFor={`js-${id}`} className="flex-col items-start gap-0.5 cursor-pointer text-sm font-normal">
                    {js.label && js.address && js.label !== js.address ? (
                      <>
                        <span className="block">{js.label}</span>
                        <span className="block text-xs text-muted-foreground">{js.address}</span>
                      </>
                    ) : (
                      js.address || js.label || 'Chantier'
                    )}
                  </Label>
                </div>
              )
            })}
            <div className="flex items-center gap-2 rounded-lg border border-dashed p-3">
              <RadioGroupItem value="__new__" id="js-new" data-testid="intervention-jobsite-new-radio" />
              <Label htmlFor="js-new" className="cursor-pointer text-sm font-normal">Une autre adresse</Label>
            </div>
          </RadioGroup>
        )}

        {needsAddress && (
          <div className="grid gap-3 sm:grid-cols-6">
            <div className="space-y-1 sm:col-span-4">
              <Label htmlFor="js-street" className="text-xs">Rue</Label>
              <Input id="js-street" data-testid="intervention-street-input" value={street} onChange={(e) => setStreet(e.target.value)} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="js-number" className="text-xs">N°</Label>
              <Input id="js-number" data-testid="intervention-street-number-input" value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="js-postal" className="text-xs">Code postal</Label>
              <Input id="js-postal" data-testid="intervention-postal-code-input" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
            </div>
            <div className="space-y-1 sm:col-span-4">
              <Label htmlFor="js-city" className="text-xs">Localité</Label>
              <Input id="js-city" data-testid="intervention-city-input" value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
          </div>
        )}
      </section>

      {/* ── Ce qu'il faut faire ────────────────────────────────────────── */}
      <section className="space-y-2">
        <Label htmlFor="intervention-description">Ce qu'il faut faire *</Label>
        <Textarea
          id="intervention-description"
          data-testid="intervention-description-input"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Porte de meuble abîmée à remplacer, électro encastré à poser…"
        />
        {/* R1 (QA 2026-08-19) — the earlier wording promised this text in the agenda and the lists.
            It is not: both show the CLIENT's name (EventBar, PlanningClientNames). Promising a
            place where the words will not appear is worse than promising nothing. */}
        <p className="text-xs text-muted-foreground">Devient le nom du dossier.</p>
      </section>

      {/* ── TVA ────────────────────────────────────────────────────────── */}
      <section className="space-y-2">
        <Label>Type de construction *</Label>
        <Select value={constructionType} onValueChange={setConstructionType}>
          <SelectTrigger data-testid="intervention-construction-type-select">
            <SelectValue placeholder="Sélectionner" />
          </SelectTrigger>
          <SelectContent>
            {INTERVENTION_CONSTRUCTION_TYPES.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label} — TVA {o.vatPercent} %</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* No default: the VAT rate ends up on an invoice, and a silent default is a silent mistake. */}
        <p className="text-xs text-muted-foreground">Détermine la TVA du dossier. Aucune valeur par défaut.</p>
      </section>

      {/* ── Les deux portes de sortie ──────────────────────────────────── */}
      {/* QA staging 2026-08-20 — a disabled button that never says why is the first cause of « ça
          marche pas ». She types four fields, clicks, and nothing happens; the blocker is often the
          job-site radio, the least visible control here. A `title` would NOT do: shadcn's
          `disabled:pointer-events-none` takes the element out of hit-testing, so the hover never
          fires (same trap as « Aperçu PDF »). It has to be VISIBLE — she is on the phone, she is
          not going to hunt for a tooltip. */}
      {missing.length > 0 && (
        <p
          data-testid="intervention-missing-hint"
          className="border-t pt-4 text-sm text-muted-foreground"
        >
          Pour continuer, il manque encore {missing.join(', ')}.
        </p>
      )}
      <div
        className={cn(
          'flex flex-col gap-2 sm:flex-row sm:justify-end',
          missing.length === 0 && 'border-t pt-4',
        )}
      >
        <Button
          variant="outline"
          disabled={!canSubmit}
          data-testid="intervention-create-quote-btn"
          onClick={() => submit('quote')}
        >
          {submitting === 'quote' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
          Faire un devis
        </Button>
        <Button
          disabled={!canSubmit}
          data-testid="intervention-create-plan-btn"
          onClick={() => submit('plan')}
        >
          {submitting === 'plan' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarPlus className="mr-2 h-4 w-4" />}
          Planifier
        </Button>
      </div>
    </div>
  )
}
